import { useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchPublicReviews } from "./reviewFeed";
import "./OathboundReviews.css";

const REVIEW_CHARACTER_LIMIT = 250;

function truncateReview(body) {
  if (body.length <= REVIEW_CHARACTER_LIMIT) return body;

  return `${body
    .slice(0, REVIEW_CHARACTER_LIMIT)
    .replace(/\s+\S*$/, "")
    .trim()}...`;
}

/**
 * Shows a horizontally browsable preview of moderated verified-purchase reviews.
 */
export default function OathboundReviews() {
  const trackRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [reviewPhase, setReviewPhase] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    fetchPublicReviews(controller.signal)
      .then((publicReviews) => {
        setReviews(publicReviews);
        setReviewPhase("ready");
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setReviewPhase("unavailable");
      });

    return () => controller.abort();
  }, []);

  const scrollReviews = (direction) => {
    const track = trackRef.current;
    if (!track) return;

    track.scrollBy({
      left: direction * Math.min(track.clientWidth * 0.78, 520),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  return (
    <section
      id="reviews"
      className="oathbound-reviews"
      aria-labelledby="oathbound-reviews-title"
    >
      <header className="oathbound-reviews-header">
        <h2 id="oathbound-reviews-title">What Readers Are Saying</h2>

        {reviews.length > 0 && (
          <div className="oathbound-review-controls" aria-label="Review controls">
            <button
              type="button"
              onClick={() => scrollReviews(-1)}
              aria-label="Scroll to previous review"
              disabled={reviews.length < 2}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollReviews(1)}
              aria-label="Scroll to next review"
              disabled={reviews.length < 2}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </header>

      {reviews.length > 0 ? (
        <div
          ref={trackRef}
          className="oathbound-reviews-track"
          tabIndex="0"
          aria-label="Verified reader review previews. Scroll horizontally to view all."
        >
          {reviews.map((review, index) => (
            <article
              className={`oathbound-review-card${
                review.backdrop ? " has-backdrop" : ""
              }`}
              key={`${review.name}-${review.favoriteCharacter}-${index}`}
            >
              {review.backdrop && (
                <img
                  className="oathbound-review-backdrop"
                  src={review.backdrop}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
              )}
              <div className="oathbound-review-content">
                <p className="oathbound-review-verified">
                  <Check aria-hidden="true" strokeWidth={2} />
                  Verified Purchase
                </p>
                <h3>{review.name}</h3>
                <p className="oathbound-review-body">
                  {truncateReview(review.body)}
                </p>
                <p className="oathbound-review-character">
                  <span>Favourite character</span>
                  <strong>{review.favoriteCharacter}</strong>
                </p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="oathbound-reviews-empty" role="status">
          <p>
            {reviewPhase === "loading"
              ? "Gathering reader reflections…"
              : reviewPhase === "unavailable"
                ? "Reader reflections are resting for a moment."
                : "The first reader reflections will appear here soon."}
          </p>
        </div>
      )}

      {reviews.length > 0 && (
        <footer className="oathbound-reviews-footer">
          <button
            type="button"
            className="oathbound-reviews-see-all"
            disabled
            aria-describedby="reviews-coming-soon"
          >
            See all reviews
          </button>
          <span id="reviews-coming-soon">Review page coming soon</span>
        </footer>
      )}
    </section>
  );
}
